import { Stack } from '@chakra-ui/react'
import FirestoreUser, {
  AdditionalDetails,
} from '../../../../types/FirestoreUser'
import {
  AlsOnsetQuestion,
  AlsOnsetClassification,
  AlsFactorsQuestions,
  SymptomsQuestions,
  ClinicalTrialsQuestions,
  OccupationQuestions,
} from './components'
import { useAdditionalDetailsForm } from './hooks'
import { handleSymptomChange } from './utils'
import useStopTrial from './components/ClinicalTrials/useStopTrial'
import { useRef, useEffect } from 'react'

interface Props {
  uid: string
  firestoreUser: FirestoreUser
}

const AdditionalDetailsTab = ({ uid, firestoreUser }: Props) => {
  const prevPropsRef = useRef({ uid, firestoreUser })
  
  useEffect(() => {
    prevPropsRef.current = { uid, firestoreUser }
  })

  const { additionalDetails, setAdditionalDetails, extraInfoRequired, update } =
    useAdditionalDetailsForm({ uid, firestoreUser })

  const stopTrial = useStopTrial(uid, additionalDetails, setAdditionalDetails)

  return (
    <Stack spacing={5}>
      <AlsOnsetQuestion
        value={additionalDetails.alsOnset || ''}
        onChange={(value) => update({ alsOnset: value })}
        isRequired={extraInfoRequired}
      />
      <AlsOnsetClassification
        classification={
          additionalDetails.alsOnsetClassification as
            | 'sporadic'
            | 'familial'
            | undefined
        }
        familialGene={additionalDetails.familialGene as string | undefined}
        onChangeClassification={(value) =>
          update({
            alsOnsetClassification: value as
              | 'sporadic'
              | 'familial'
              | undefined,
            familialGene: undefined,
          })
        }
        onChangeFamilialGene={(value) => update({ familialGene: value })}
        isRequired={extraInfoRequired}
      />

      <AlsFactorsQuestions
        contributingFactors={additionalDetails.contributingFactors || {}}
        update={(factors) =>
          update({
            contributingFactors: {
              ...additionalDetails.contributingFactors,
              ...factors,
            },
          })
        }
        isRequired={extraInfoRequired}
      />

      <SymptomsQuestions
        onChangeSymptom={(index, symptom) =>
          handleSymptomChange(index, symptom, additionalDetails, update)
        }
        additionalDetails={additionalDetails as AdditionalDetails}
        isRequired={extraInfoRequired}
      />

      <OccupationQuestions
        userId={uid}
        additionalDetails={additionalDetails}
        setAdditionalDetails={setAdditionalDetails}
      />

      <ClinicalTrialsQuestions
        additionalDetails={additionalDetails as AdditionalDetails}
        setAdditionalDetails={setAdditionalDetails}
        update={update}
        firestoreUser={firestoreUser}
        isRequired={extraInfoRequired}
        uid={uid}
        stopTrial={stopTrial}
      />
    </Stack>
  )
}

export default AdditionalDetailsTab
